import {neverland as $, html, useState} from 'neverland';
import { completeNewPassword, extractGroupsFromToken, hasIdToken } from '../lib/cognito.js';
import { resolveLoginDecision } from '../lib/clusterAccess.js';
import { sendApprovalNotification } from '../lib/approval.js';
import { PasswordChecklist, passwordMeetsAllRules } from '../lib/passwordRules.js';

const NewPasswordPage = $(function(auth, setAuth, theme) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pw, setPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  function getLogo() {
    if (theme && theme.logo && theme.logo !== '') {
      return '/static/img/logos-large/' + theme.logo;
    }
    return '/static/img/transparent-placeholder.png';
  }

  async function handleSubmit() {
    setError('');
    setLoading(true);

    const pw1 = document.querySelector('#new-password').value;
    const pw2 = document.querySelector('#confirm-new-password').value;

    if (!passwordMeetsAllRules(pw1)) {
      setError('Password does not meet all the requirements shown below.');
      setLoading(false);
      return;
    }

    if (pw1 !== pw2) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      // Resolves with a session if no MFA follows; rejects with an MFA
      // challenge (email code sent) otherwise.
      const session = await completeNewPassword(auth.mfaChallenge, pw1);

      // Apply the shared login-completion gate before entering the portal.
      // Prefer groups extracted from the freshly-resolved session; fall back to
      // any groups already on auth if the session isn't token-shaped here.
      const groups = extractGroupsFromToken(session);
      const resolvedGroups = (Array.isArray(groups) && groups.length > 0) ? groups : (auth.cognitoGroups || []);

      // reg-mfa-notify fix: this resolve path means completeNewPassword returned
      // a session WITHOUT a following MFA challenge — the user is logged in, so a
      // valid ID token exists here. If this is the tail of a REGISTRATION (the
      // stash is present, carried over from RegisterPage's NEW_PASSWORD_REQUIRED
      // branch), send the approval notification now, gated on the stash's
      // PRESENCE so an ordinary login never sends. When completeNewPassword
      // instead throws an MFA challenge (the usual case for this pool), the stash
      // is preserved by the `...prev` spread in the catch branches below and
      // MFASetupPage / MFAVerifyPage perform the send after MFA completes.
      const isRegistration = !!(auth.registrationEntry && auth.registrationClientGroupId);
      if (isRegistration) {
        sendApprovalNotification(auth, auth.registrationEntry, auth.registrationClientGroupId);
      }

      const decision = await resolveLoginDecision({ email: auth.userEmail, groups: resolvedGroups, userData: auth.userData, db: auth.db, isTokenReady: hasIdToken });

      if (decision.action === 'revoked') {
        setError(decision.message);
        setLoading(false);
        return;
      } else if (decision.action === 'register') {
        setAuth(prev => ({
          ...prev,
          mfaPage: 'ConfirmOrgAccess',
          userPassword: pw1,
          mfaChallenge: null,
          cognitoGroups: resolvedGroups,
          registrationEntry: null,
          registrationClientGroupId: null
        }));
        setLoading(false);
        return;
      } else if (decision.action === 'pending') {
        setAuth(prev => ({
          ...prev,
          mfaPage: 'PendingApproval',
          userPassword: pw1,
          mfaChallenge: null,
          cognitoGroups: resolvedGroups,
          registrationEntry: null,
          registrationClientGroupId: null
        }));
        setLoading(false);
        return;
      }

      setAuth(prev => ({
        ...prev,
        mfaPage: null,
        mfaVerified: true,
        clusterAccess: decision.clusterAccess,
        userPassword: pw1,
        mfaChallenge: null,
        cognitoGroups: resolvedGroups,
        registrationEntry: null,
        registrationClientGroupId: null
      }));
    } catch (err) {
      if (err.type === 'MFA_SETUP') {
        setAuth(prev => ({
          ...prev,
          mfaPage: 'MFASetup',
          mfaChallenge: err,
          userPassword: pw1
        }));
      } else if (err.type === 'MFA_REQUIRED') {
        setAuth(prev => ({
          ...prev,
          mfaPage: 'MFAVerify',
          mfaChallenge: err,
          userPassword: pw1
        }));
      } else {
        setError(`Failed to set password: ${err.message || 'Unknown error'}`);
      }
    }

    setLoading(false);
  }

  function handleKeyPress(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      handleSubmit();
    }
  }

  function goBack() {
    setAuth(prev => ({ ...prev, mfaPage: null, cognitoUser: null, userData: null }));
  }

  return html`
    <div class="centre-box login">
      <img src="${getLogo()}" class="squiggle logo-large" />

      <div id="login">
        <h2 style="margin-bottom: 10px; font-size: 1.2em;">Set New Password</h2>
        <p style="margin-bottom: 12px; font-size: 1em; opacity: 0.8;">
          You need to set a new password before continuing.
        </p>

        <span class="loginlabel">NEW PASSWORD</span>
        <input type="password" id="new-password" class="login-field" placeholder="Choose a new password" oninput=${(e) => setPw(e.target.value)} /><br />

        ${PasswordChecklist(pw, confirmPw)}

        <span class="loginlabel">CONFIRM PASSWORD</span>
        <input type="password" id="confirm-new-password" class="login-field" placeholder="Confirm new password" oninput=${(e) => setConfirmPw(e.target.value)} onkeyup=${handleKeyPress} /><br />

        <button id="login-submit" onclick=${handleSubmit} disabled=${loading || !passwordMeetsAllRules(pw) || pw !== confirmPw}>
          ${loading ? 'Setting password...' : html`<i class="fal fa-key"></i>&ensp;SET PASSWORD`}
        </button>

        <button class="back-link" onclick=${goBack}>
          &larr; Back to Login
        </button>

        ${error ? html`<div class="error"><strong>ERROR</strong><p>${error}</p></div>` : ''}
      </div>
    </div>
  `;
});

export default NewPasswordPage;
